import React, {useEffect, useMemo, useRef} from 'react';
import {
  useBuyerManagement,
  useSubstituteItemDetailsContext,
} from '@buncha/itemDetails/hooks/SubstituteItemDetail';

import {Box} from '../../../../components/core/view';
import Carousal from '../../../../components/composites/Carousal/Carousal';
import If from '../../../../components/conditional/If';
import ItemInfo from '../ItemInfo/ItemInfo';
import ItemQuantityPill from '../ItemQuantityPill/index';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import {ScrollView} from 'react-native';
import SubsBuyerDetails from '../SubsBuyerDetails/SubsBuyerDetails';
import {getDefaultImageUrl} from '../../../../utils/common';
import {styles} from './styles';
import {useRoute} from '@react-navigation/native';

interface RouteParamsType {
  storeIndex?: string;
  catalogItemId?: number;
  orderItemId?: number;
  runId?: number;
}

interface Props {
  setLoading: (loading: boolean) => void;
  loading?: boolean;
  bottomButtonLoading?: boolean;
  onButtonPress: (sMasterId?: number) => void;
  buttonText: string;
  isRequestAcceptedByBuyer?: boolean;
  subsDetailsBody: JSX.Element;
}
const SubstituteItemDetailsBody = (props: Props) => {
  const {state, dispatch} = useSubstituteItemDetailsContext();
  const route = useRoute();
  const {getBuyersForItem, loadingBuyer} = useBuyerManagement(dispatch);
  const runState = state?.runData;
  const routeParams: RouteParamsType | undefined = route?.params;
  const orderItemId = Number(routeParams?.orderItemId) || 0;
  const isCustomItem = Boolean(!state?.item?.itemImageUrls);
  const isMenu = Boolean(!runState?.store?.elasticSearchIndex);
  const pageLoading = state?.runDataLoading;
  const scrollRef = useRef<ScrollView>(null);

  const imageData = useMemo(() => {
    if (isCustomItem)
      return [state?.item?.itemImageUrl || getDefaultImageUrl()];
    return state?.item?.itemImageUrls?.largeImages;
  }, [
    isCustomItem,
    state?.item?.itemImageUrl,
    state?.item?.itemImageUrls?.largeImages,
  ]);

  useEffect(() => {
    if (orderItemId && routeParams?.catalogItemId)
      getBuyersForItem(runState?.id, orderItemId, routeParams?.catalogItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemId]);

  useEffect(() => {
    props.setLoading(pageLoading);
  }, [pageLoading, props]);

  const getLoadingState = useMemo(
    () => pageLoading || !state?.item,
    [pageLoading, state?.item],
  );

  return (
    <ScrollView
      ref={scrollRef}
      contentContainerStyle={
        getLoadingState ? styles.contentContainer : undefined
      }>
      <React.Fragment>
        <If condition={getLoadingState || props.loading}>
          <LoaderComponents showBottomText={false} />
        </If>
        <If condition={!getLoadingState}>
          <Box style={styles.bodyContainer}>
            <If condition={state.item?.itemQuantity}>
              <Box style={[styles.pillBody]}>
                <ItemQuantityPill
                  quantity={state.item?.itemQuantity as string}
                />
              </Box>
            </If>
            <Carousal
              containerStyle={styles.itemContent}
              imageContainerStyle={styles.itemImage}
              showImageCaption={false}
              data={imageData}
              showPagination={true}
            />
          </Box>
          <Box style={styles.childContainer}>
            <ItemInfo
              isUPCMode={false}
              item={state?.item}
              upcItemId={orderItemId}
              orderItemId={orderItemId}
              esIndex={runState?.store?.elasticSearchIndex ?? ''}
              isItemDetailPage={false}
              storeId={runState?.store?.id}
            />
            <If condition={props.subsDetailsBody}>{props.subsDetailsBody}</If>
            <Box>
              <SubsBuyerDetails
                isMenu={isMenu}
                isCustomItem={isCustomItem}
                loadingBuyer={loadingBuyer}
              />
            </Box>
          </Box>
        </If>
      </React.Fragment>
    </ScrollView>
  );
};

export default React.memo(SubstituteItemDetailsBody);
